import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { PlaylistItem } from '@jwp/ott-common/types/playlist';
import { getModule } from '@jwp/ott-common/src/modules/container';
import { useFavoritesStore } from '@jwp/ott-common/src/stores/FavoritesStore';
import FavoritesController from '@jwp/ott-common/src/controllers/FavoritesController';
import Favorite from '@jwp/ott-theme/assets/icons/favorite.svg?react';
import FavoriteBorder from '@jwp/ott-theme/assets/icons/favorite_border.svg?react';
import useBreakpoint, { Breakpoint } from '@jwp/ott-ui-react/src/hooks/useBreakpoint';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { useUIControls } from '@jwp/ott-hooks-react/src/useUIControls';
import { useAccountStore } from '@jwp/ott-common/src/stores/AccountStore';
import { useQuery, useMutation } from '@apollo/client/react';
import { GET_FAVORITE_BY_JW_MEDIA_ID, GetFavoriteByJWMediaIdData } from '@jwp/ott-common/src/graphql/query/favorite.queries';
import { INSERT_FAVORITES, DELETE_FAVORITES } from '@jwp/ott-common/src/graphql/mutation/favorite.mutations';

import Button from '../../components/Button/Button';
import Alert from '../../components/Alert/Alert';
import Icon from '../../components/Icon/Icon';
import { useTheme } from '../../../../../ThemeContext';
import { Auth } from 'aws-amplify';
import { setLocalStorage } from '@jwp/ott-common/src/utils/localstorage';
import { useLocation, useNavigate } from 'react-router';
import { modalURLFromLocation } from '../../utils/location';
import { sendGAEvent } from '@jwp/ott-hooks-react/src/useAnalytics';
import HeartIcon from '@jwp/ott-theme/assets/icons/component-icons/HeartIcon';

type Props = {
  item: PlaylistItem;
};

const FavoriteButton: React.VFC<Props> = ({ item }) => {
  const { t } = useTranslation();
  const breakpoint = useBreakpoint();
  const { universalStyles } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const favoritesController = getModule(FavoritesController);
  const { isFavoriteEnable, isCognitoEnabled, isCustomApiEnabled, isKatonCognitoEnabled, isShowLoginButton, isShowRegisterButton, isAuthenticationEnabled } = useAppConfig();
  const { buttonIconOnly, buttonLabelOnly, buttonLabelWithIcon } = useUIControls();
  const isLoggedIn = !!useAccountStore(({ user }) => user);

  // Default
  const { isFavoriteLocal, clearWarning, warning } = useFavoritesStore((state) => ({
    isFavoriteLocal: !!item && state.hasItem(item),
    clearWarning: state.clearWarning,
    warning: state.warning,
  }));

  // Cognito/GraphQL logic
  const { data, loading: favoritesLoading, refetch } = useQuery<GetFavoriteByJWMediaIdData>(GET_FAVORITE_BY_JW_MEDIA_ID, {
    variables: { jwMediaId: item.mediaid },
    skip: !isLoggedIn || !item.mediaid || (isCognitoEnabled === false && isKatonCognitoEnabled === false),
    fetchPolicy: 'network-only'
  });

  const [insertFavorite] = useMutation(INSERT_FAVORITES);
  const [deleteFavorite] = useMutation(DELETE_FAVORITES);

  const isFavorite = (isCognitoEnabled || isKatonCognitoEnabled) ? !!data?.getFavouriteByJWmediaid : isFavoriteLocal;
  const isLoading = (isCognitoEnabled || isKatonCognitoEnabled) && favoritesLoading;

  const onFavoriteButtonClick = useCallback(async () => {
    if (!isLoggedIn && isCognitoEnabled && isCustomApiEnabled) {
      await setLocalStorage('redirectURL', location.pathname);
      await Auth.federatedSignIn();
      return;
    }

    if (!isLoggedIn && isAuthenticationEnabled) {
      if (isKatonCognitoEnabled) {
        navigate('/login');
      } else {
        navigate(modalURLFromLocation(location, 'create-account'));
      }
      return;
    }

    if (isCognitoEnabled || isKatonCognitoEnabled) {
      try {
        if (isFavorite) {
          await deleteFavorite({ variables: { jwMediaId: item.mediaid } });
        } else {
          await insertFavorite({ variables: { jwMediaId: item.mediaid } });
          sendGAEvent('add_to_wishlist', {
            content_id: item.mediaid,
            content_title: item.title,
          });
        }
        refetch();
      } catch (error) {
        console.error('Failed to toggle favorite', error);
      }
    } else {
      await favoritesController.toggleFavorite(item);
      sendGAEvent('add_to_wishlist', {
        content_id: item.mediaid,
        content_title: item.title,
      });
    }
  }, [item, isLoggedIn, isFavorite, isCognitoEnabled, isKatonCognitoEnabled, isCustomApiEnabled, isAuthenticationEnabled, insertFavorite, deleteFavorite, refetch, favoritesController, location, navigate]);

  useEffect(() => {
    return () => clearWarning();
  }, [clearWarning]);

  return (
    <>
      {isFavoriteEnable && (isShowLoginButton || isShowRegisterButton) && isAuthenticationEnabled ? (
        <>
          <Button
            label={buttonLabelOnly || buttonLabelWithIcon ? t('video:favorite') : undefined}
            variant="contained"
            size="exlarge"
            aria-label={t('video:favorite')}
            startIcon={
              buttonIconOnly || buttonLabelWithIcon ? (
                isFavorite ? (
                  <HeartIcon fillColor={universalStyles.helpValueText.color} />
                ) : (
                  <Icon icon={FavoriteBorder} />
                )
              ) : undefined
            }
            onClick={onFavoriteButtonClick}
            aria-pressed={isFavorite}
            color="default"
            fullWidth={breakpoint < Breakpoint.md}
            textStyle={universalStyles.favoriteAndShareButtonText}
            disabled={isLoading}
          />
          <Alert open={warning !== null} message={warning} onClose={clearWarning} />
        </>
      ) : null}
    </>
  );
};

export default FavoriteButton;
